import { formatNumber } from '@angular/common';
import { Component, effect, inject, input, OnDestroy, OnInit, output } from '@angular/core';
import { LMapService } from 'app/shared/ll/services';
import {
    DivIcon,
    DomEvent,
    GeoJSON,
    Icon,
    IconOptions,
    LatLng,
    Layer,
    LeafletMouseEvent,
    Marker,
    PathOptions,
    Polygon,
    Polyline,
} from 'leaflet';
import polylabel from 'polylabel';
import {
    distanceBetweenPoints,
    Geometry,
    getCenterOfPoints,
    getLinesFromRing,
    isMultiPolygon,
    isPolygon,
    Position
} from '../models/geometry';
import { Distance } from '../models/units';
import { LFeatureGroupComponent } from './l-feature-group.component';


interface NumberedDivIconOptions extends IconOptions {
    number?: number | string;
}

export class NumberedDivIcon extends Icon {

    options: NumberedDivIconOptions;

    constructor(options: NumberedDivIconOptions) {
        super({
            iconSize: [25, 41],
            iconAnchor: [13, 41],
            popupAnchor: [0, -33],
            className: 'leaflet-number-icon',
            ...options,
        } as IconOptions);
    }

    createIcon() {
        const div = document.createElement('div');
        // @ts-ignore
        const img = this['_createImg'](this.options.iconUrl);
        const numdiv = document.createElement('div');
        numdiv.setAttribute('class', 'number');
        numdiv.innerHTML = `${this.options.number}`;
        div.appendChild(img);
        div.appendChild(numdiv);
        // @ts-ignore
        this['_setIconStyles'](div, 'icon');
        return div;
    }
}

export class NumberedSvgIcon extends Icon {

    options: NumberedDivIconOptions;

    constructor(options: NumberedDivIconOptions) {
        super({
            iconSize: [38, 62],
            iconAnchor: [19, 62],
            popupAnchor: [0, -33],
            className: 'leaflet-number-icon',
            ...options,
        } as IconOptions);
    }

    createIcon() {
        const div = document.createElement('div');
        const img = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        // @ts-ignore
        img.setAttribute('height', this.options.iconSize![1]);
        // @ts-ignore
        img.setAttribute('width', this.options.iconSize![0]);
        const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
        use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', this.options.iconUrl);
        img.appendChild(use);
        const numdiv = document.createElement('div');
        numdiv.setAttribute('class', 'svg-number');
        numdiv.innerHTML = `${this.options.number}`;
        div.appendChild(img);
        div.appendChild(numdiv);
        // @ts-ignore
        this['_setIconStyles'](div, 'icon');
        return div;
    }
}

export class CustomImageIcon extends Icon {

    constructor(options?: IconOptions) {
        super({
            iconUrl: Icon.prototype.options.iconUrl,
            className: 'leaflet-custom-icon',
            ...options,
        });
    }

    createIcon() {
        const div = document.createElement('div');
        // @ts-ignore
        const img = this['_createImg'](this.options.iconUrl);
        div.appendChild(img);
        // @ts-ignore
        this['_setIconStyles'](div, 'icon');
        return div;
    }
}


@Component({
    selector: 'l-feature',
    styles: [
        `
        :host:focus, :host:active {
            outline: none;
        }
        `,
    ],
    template: '',
})
export class LFeatureComponent implements OnInit, OnDestroy {

    id = input<string>();

    geometry = input.required<Geometry>();

    style = input<PathOptions>({});

    data = input();

    editable = input(false);

    displayLegendMarker = input(false);

    displayDistanceMarker = input(false);

    legendText = input<string>('');

    select = output<Position>();

    private legendMarker: Marker | null = null;

    private feature: Layer;

    private distanceMarkers: Marker[] = [];

    private mapService = inject(LMapService);

    private featureGroup = inject(LFeatureGroupComponent, { host: true, optional: true });

    constructor() {
        effect(() => {
            const style = this.style();

            if (this.feature instanceof Polyline) {
                this.feature.setStyle(style);
            }
        });
        effect(() => {
            if (this.displayLegendMarker()) {
                const geometry = this.geometry();
                if (geometry.type !== 'Point') {
                    const center = this.getCenter(geometry);
                    const text = this.legendText();
                    if (center && text) {
                        if (this.legendMarker) {
                            if (this.featureGroup) {
                                this.featureGroup.group.removeLayer(this.legendMarker);
                            } else {
                                this.legendMarker.remove();
                            }
                            this.legendMarker = null;
                        }

                        this.legendMarker = this.createLabelMarker(text, center);
                        if (this.featureGroup) {
                            this.featureGroup.addToGroup(this.legendMarker);
                        } else {
                            this.mapService.addFeature(this.legendMarker);
                        }
                    }
                }
            } else {
                if (this.legendMarker) {
                    if (this.featureGroup) {
                        this.featureGroup.group.removeLayer(this.legendMarker);
                    } else {
                        this.legendMarker.remove();
                    }
                    this.legendMarker = null;
                }
            }
        });

        effect(() => {
            if (!this.displayDistanceMarker()) {
                this.distanceMarkers.forEach(marker => {
                    marker.remove();
                });
                return;
            }

            const geometry = this.geometry();
            if (geometry.type !== 'Polygon') {
                return;
            }

            const lines = getLinesFromRing(geometry.coordinates[0]);
            for (const [x, y] of lines) {
                const center = this.lineCenter(x, y);
                const distance = distanceBetweenPoints(x, y);

                if (distance < 0.01) {
                    continue;
                }

                const dist = new Distance(distance);
                const i = dist.getQuantity(true);
                const m = dist.getQuantity(false);
                const iValue = formatNumber(i.value, 'en_US', '1.2-2');
                const mValue = formatNumber(m.value, 'en_US', '1.2-2');
                const distanceFormatted = `${iValue} ${i.unit} / ${mValue} ${m.unit}`;
                const marker = this.createDistanceLabelMarker(distanceFormatted, center);
                this.distanceMarkers.push(marker);
                if (this.featureGroup) {
                    this.featureGroup.addToGroup(marker);
                } else {
                    this.mapService.addFeature(marker);
                }
            }
        });
    }

    ngOnInit() {
        const { coordinates, type } = this.geometry();
        switch (type) {
            case 'Point':
            case 'MultiPoint':
                // for markers use l-markers-group component
                return;
            case 'LineString':
            case 'MultiLineString': {
                const latLngs = GeoJSON.coordsToLatLngs(coordinates, type === 'LineString' ? 0 : 1, GeoJSON.coordsToLatLng);
                this.feature = new Polyline(latLngs, this.style());
                break;
            }
            case 'Polygon':
            case 'MultiPolygon': {
                const latLngs = GeoJSON.coordsToLatLngs(coordinates, type === 'Polygon' ? 1 : 2, GeoJSON.coordsToLatLng);
                this.feature = new Polygon(latLngs, this.style());
                break;
            }
            default:
                console.error(`invalid geometry (${this.geometry()})`);
                return;
        }

        // @ts-ignore
        this.feature['feature'] = { id: this.id(), geometry: this.geometry() };

        this.feature.on('click', (event: LeafletMouseEvent) => {
            DomEvent.stopPropagation(event);
            this.select.emit([event.latlng.lng, event.latlng.lat]);
        });

        if (this.featureGroup) {
            this.featureGroup.addToGroup(this.feature);
        } else {
            this.mapService.addFeature(this.feature);
        }
    }

    private polygonCenter(coords: number[][][]) {
        const center = polylabel(coords, 1.0);
        return [center[1], center[0]];
    }

    private lineCenter(point1: Position, point2: Position) {
        const center = getCenterOfPoints(point1, point2);
        return [center[1], center[0]];
    }

    private getCenter(geometry: Geometry) {
        if (isPolygon(geometry)) {
            return this.polygonCenter(geometry.coordinates);
        }
        if (isMultiPolygon(geometry)) {
            return this.polygonCenter(geometry.coordinates[0]);
        }
        return false;
    }

    private createDistanceLabelMarker(text: string, center: number[]) {
        const icon = new DivIcon({
            html: text,
            className: 'distance-label',
        });
        return new Marker(new LatLng(center[0], center[1]), { icon, interactive: false });
    }

    private createLabelMarker(text: string, center: number[]) {
        const icon = new DivIcon({
            html: text,
            className: 'legend-label',
        });
        return new Marker(new LatLng(center[0], center[1]), { icon, interactive: false });
    }

    ngOnDestroy() {
        if (this.feature) {
            this.feature.off();
            this.feature.remove();
            if (this.legendMarker) {
                if (this.featureGroup) {
                    this.featureGroup.group.removeLayer(this.legendMarker);
                } else {
                    this.legendMarker.remove();
                }
                this.legendMarker = null;
            }
            this.distanceMarkers.forEach(value => {
                value.remove();
            });
        }
    }
}
